import React from 'react';
import type { ApprovalStatus, FinanceStatus, TierRole } from '../../services/emailWorkflowApi';

/**
 * Status / tier pills for the email approval dashboard.
 * Colors follow UI_CONTRACT §5. Self-contained (the shared <Badge> has a fixed
 * enum that doesn't cover these states), so this is the one place the mapping lives.
 */

const pill = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap';

const APPROVAL: Record<ApprovalStatus, { label: string; cls: string }> = {
	pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-600' },
	approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
	rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const FINANCE: Record<FinanceStatus, { label: string; cls: string }> = {
	awaiting: { label: 'Awaiting', cls: 'bg-gray-100 text-gray-600' },
	paid: { label: 'Paid', cls: 'bg-blue-100 text-blue-700' },
	proof_attached: { label: 'Proof attached', cls: 'bg-teal-100 text-teal-700' },
	done: { label: 'Done', cls: 'bg-green-100 text-green-700' },
	recheck: { label: 'Recheck', cls: 'bg-amber-100 text-amber-700' },
};

// current_tier_role → friendly "where is it" label (plain words, owner wording 2026-07-05).
const TIER: Record<'tier1' | 'tier2' | 'accounts', { label: string; cls: string }> = {
	tier1: { label: 'Waiting: 1st approver', cls: 'bg-indigo-100 text-indigo-700' },
	tier2: { label: 'Waiting: 2nd approver', cls: 'bg-purple-100 text-purple-700' },
	accounts: { label: 'With accounts', cls: 'bg-cyan-100 text-cyan-700' },
};

export const ApprovalBadge: React.FC<{ status: ApprovalStatus; source?: string | null }> = ({
	status,
	source,
}) => {
	const m = APPROVAL[status] ?? APPROVAL.pending;
	return (
		<span className={`${pill} ${m.cls}`} title={source ? `via ${source}` : undefined}>
			{m.label}
			{source === 'email' && <span className="opacity-60">✉︎</span>}
		</span>
	);
};

export const FinanceBadge: React.FC<{ status: FinanceStatus }> = ({ status }) => {
	const m = FINANCE[status] ?? FINANCE.awaiting;
	return <span className={`${pill} ${m.cls}`}>{m.label}</span>;
};

/**
 * Tier badge. Un-routed rows (role null) with an approval recorded read as
 * "Approved / Done"; role null with no approval → nothing (historical row).
 */
export const TierBadge: React.FC<{
	role: TierRole;
	effectiveApproved?: boolean;
	/** When paid/done the journey is over — say "Done", not "With accounts"
	 *  (the backend never advances the chain pointer past accounts). */
	financeStatus?: string | null;
}> = ({
	role,
	effectiveApproved,
	financeStatus,
}) => {
	if (financeStatus === 'paid' || financeStatus === 'done') {
		return <span className={`${pill} bg-green-100 text-green-700`}>Done</span>;
	}
	if (role && TIER[role]) {
		const m = TIER[role];
		return <span className={`${pill} ${m.cls}`}>{m.label}</span>;
	}
	if (effectiveApproved) {
		return <span className={`${pill} bg-green-100 text-green-700`}>Approved / Done</span>;
	}
	return <span className="text-xs text-gray-300">—</span>;
};

// ── label maps ────────────────────────────────────────────────────────────
export const FACILITY_LABEL: Record<string, string> = {
	blr_sng: 'Bangalore SNG',
	blr_mhd: 'Bangalore MHD',
	hyd: 'Hyderabad',
	ggn: 'Gurgaon',
	mumbai: 'Mumbai',
};

// City dropdown options (label → city_id). "All" omits the param.
export const CITY_OPTIONS: { label: string; value: number | '' }[] = [
	{ label: 'All cities', value: '' },
	{ label: 'Bangalore', value: 2 },
	{ label: 'Hyderabad', value: 5 },
	{ label: 'Mumbai', value: 3 },
	{ label: 'Gurgaon', value: 4 },
	{ label: 'Pune', value: 1 },
	{ label: 'Chennai', value: 7 },
];

export const facilityLabel = (key: string | null | undefined): string =>
	key ? FACILITY_LABEL[key] ?? key : '';
